import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  QueryKey,
  QueryObserverResult,
  RefetchOptions,
  RefetchQueryFilters,
  UseQueryOptions,
  useQuery
} from 'react-query';
import { useHistory } from 'react-router-dom';
import settings from '../config/settings';
import UIStore from '../stores/UIStore';

// ─── Interfaces ─────────────────────────────────────────────────────────────

export interface IServerCallErrorResponse {
  code: number;
  params: any;
  desc: string;
  description?: string | null;
}

interface IErrorDetail {
  code: number;
  description: string;
  domain: string;
  error: string;
  id: string;
  name: string;
  params?: any[];
  stack: any[];
}

interface IErrorResponse {
  errors: IErrorDetail[];
  success: boolean;
  summary: string;
  time: string;
  _type: string;
}

interface IUseServerCallProps<T, K> {
  key: QueryKey;
  /** default: 'auto' */
  mode?: 'auto' | 'control';
  request: AxiosRequestConfig<T>;
  queryOptions?: Omit<UseQueryOptions<K, AxiosError<K, T>, K, QueryKey>, 'queryKey' | 'queryFn'>;
  disableSnackError?: boolean;
  APIBaseURL?: string;
  onSuccess?: (response: AxiosResponse<K, T>, request?: AxiosRequestConfig<T>) => void;
  onError?: (error: string, errors?: IServerCallErrorResponse[]) => void;
  disabled?: boolean;
  disableServerError?: boolean;
}

export interface IUseServerCall<TReq, TRes> {
  data: TRes | undefined;
  isPending: boolean;
  isError: boolean;
  run: (request?: Partial<AxiosRequestConfig<TReq>>) => void;
  runAsync: (request?: Partial<AxiosRequestConfig<TReq>>) => Promise<TRes | undefined>;
  refetch: <TPageData>(
    options?: RefetchOptions & RefetchQueryFilters<TPageData>
  ) => Promise<QueryObserverResult<TRes, AxiosError<TRes, TReq>>>;
}

// ─── useServerCall ───────────────────────────────────────────────────────────

export const useServerCall = <TReq extends unknown, TRes extends unknown>(
  props: IUseServerCallProps<TReq, TRes>
): IUseServerCall<TReq, TRes> => {
  const {
    key,
    mode = 'auto',
    request,
    queryOptions,
    disabled = false,
    onError,
    onSuccess,
    disableServerError = false
  } = props;

  const [control, setControl] = useState(false);
  const [dynamicRequest, setDynamicRequest] = useState<AxiosRequestConfig<TReq>>(request);
  const history = useHistory();

  const handleAxiosError = useCallback(
    (catchedErr: any): never => {
      if (axios.isAxiosError(catchedErr)) {
        const errResponse = (catchedErr as AxiosError<TRes, TReq>).response;

        const errData = errResponse?.data as IErrorResponse | undefined;

        const isUnauthorized =
          errResponse?.status === 401 ||
          (errData?.errors?.some((e) => Math.abs(e.code) === 1207) ?? false) ||
          (!errResponse && !disableServerError);

        if (isUnauthorized && history.location.pathname !== '/login') {
          history.push('/login');
        }

        const errorDescription = errData?.errors?.[0]?.description ?? '';

        const errors = errData?.errors?.map((x) => {
          return {
            code: x.code,
            desc: x.description ?? 'Unknown error',
            params: x.params?.reduce((acc: any, val: any, index: number) => {
              acc[`param${index}`] = val;
              return acc;
            }, {})
          };
        });

        if (!disableServerError && errorDescription) {
          UIStore.setNotificationMessage(errorDescription, 'error');
        }

        if (errorDescription) {
          if (onError) {
            onError(errorDescription, errors);
          }
          throw new Error(errorDescription);
        } else {
          if (onError) {
            onError('Unknown error', errors);
          }
        }
        throw new Error('Unknown error: ' + JSON.stringify(catchedErr.toJSON()));
      } else {
        throw new Error(catchedErr ?? 'Unknown error');
      }
    },
    [history, disableServerError, onError]
  );

  const axiosRequest = useCallback(async () => {
    if (mode === 'control') setControl(false);
    let axiosInstance = axios.create();
    let response: AxiosResponse<TRes, TReq> | null = null;
    try {
      // Public URL requests are without auth header
      if (settings.API_PUBLIC_URL && dynamicRequest?.url?.includes(settings.API_PUBLIC_URL)) {
        dynamicRequest.headers = {
          ...dynamicRequest.headers,
          Authorization: ''
        };
      }

      response = await axiosInstance.request<TRes, AxiosResponse<TRes, TReq>, TReq>(dynamicRequest);
      if (onSuccess) {
        onSuccess(response, dynamicRequest);
      }
      return response.data;
    } catch (catchedErr: any) {
      return handleAxiosError(catchedErr);
    }
  }, [dynamicRequest, onSuccess, mode, handleAxiosError]);

  const { data, isFetching, isLoading, isError, refetch } = useQuery<TRes, AxiosError<TRes, TReq>>(
    key,
    axiosRequest,
    Object.assign({}, queryOptions, mode === 'control' ? { enabled: !disabled && control } : { enabled: !disabled }, {
      retry: false
    })
  );

  const run = useCallback(
    (req?: Partial<AxiosRequestConfig<TReq>>) => {
      if (mode === 'control') {
        if (req) {
          setDynamicRequest((previousRequest) => Object.assign({}, previousRequest, req));
        }
        setControl(true);
      }
    },
    [mode, request, dynamicRequest]
  );

  const runAsync = useCallback(
    async (request?: Partial<AxiosRequestConfig<TReq>>) => {
      const nextReq = Object.assign({}, dynamicRequest, request);

      if (mode === 'control') {
        if (request) {
          try {
            let axiosInstance = axios.create();
            const response = await axiosInstance.request<TRes, AxiosResponse<TRes, TReq>, TReq>(nextReq);
            return response.data;
          } catch (catchedErr: any) {
            // Rovnaké spracovanie ako v auto móde — vrátane presmerovania na /login pri 401
            return handleAxiosError(catchedErr);
          }
        }
      }
    },
    [mode, dynamicRequest, handleAxiosError]
  );

  return { data, isPending: isFetching || isLoading, isError, run, runAsync, refetch };
};

// ─── useError ────────────────────────────────────────────────────────────────
// Used by XsTextField / XsNumberField to read react-hook-form errors

export const useError = (name?: string) => {
  const { t } = useTranslation();
  const form = useFormContext();

  const getValue = (obj: any, name?: string) => {
    if (obj === undefined || obj === null || name === undefined) return null;
    let value = obj;
    if (typeof value === 'object') {
      for (const prop of name.split('.')) {
        if (value) {
          value = value[prop];
        }
      }
    }
    return value;
  };

  let formStateErrors = getValue(form?.formState?.errors, name);
  let error: boolean = false;
  let helperText: string[] = [];

  if (formStateErrors) {
    formStateErrors = Array.isArray(formStateErrors) ? formStateErrors : [formStateErrors];

    error = formStateErrors.some((formStateError: any) => formStateError !== undefined);

    formStateErrors.forEach((formStateError: any) => {
      if (formStateError.type) {
        const translationArgs = {
          name: t(`validation.${name}`),
          index: formStateError.message.substring(
            formStateError.message.indexOf('[') + 1,
            formStateError.message.lastIndexOf(']')
          )
        };
        helperText.push(
          t(
            [`validation.${formStateError.message}`, `validation.${formStateError.type}`, `validation.${name}`],
            translationArgs
          )
        );
      } else if (formStateError.message) {
        helperText.push(formStateError.message);
      }
    });
  }
  return { error, helperText };
};

// ─── useDebounce ───────────────────────────────────────────────────────────────
// Port zo src/global/helpers/hooks.tsx — používa ho XsAutocompleteServerWithoutLoad

export const useDebounce = <T extends unknown>(value: T, delay?: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay ?? 500);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};
